import { calc, PotentialBet } from "@ppb/bet-engine";
import { createSelector, OutputParametricSelector } from "reselect";
import { ExchangeBettingBonus, EligibleBonus, BonusDiscount } from "./ExchangeBettingBonus.types";
import { getExchangeRunnerTree } from "../exchange-markets/exchange-markets-selectors";
import { type BetslipExchangeRunner, type BetslipExchangeRunnerTree } from "../exchange-markets/ExchangeMarkets.types";
import { calculateEligibleBonus } from "../../../helpers/free-bets";
import { createRunnerPotentialBetsSelector } from "../exchange-betting/runner-potential-bets-selectors";
import type { BetslipApplicationState } from "../BetslipApplicationState";

/** ********************************
 *  Bonus wallets state selectors  *
 ********************************* */

/**
 * For a given market id, returns the corresponding market bonus wallets or null if there's none
 */
export const getExchangeBettingBonusByMarketId = (
  state: BetslipApplicationState,
  marketId: string,
): ExchangeBettingBonus | null => {
  const marketBonus = state.exchangeBettingBonus;

  if (!marketBonus || marketId !== marketBonus.marketId) {
    return null;
  }

  return marketBonus;
};

/**
 * For a given market id, returns the eligible market bonus or 0 if there's none
 */
export const getExchangeEligibleBonusByMarketId = (
  state: BetslipApplicationState,
  marketId: string,
  selectionId: number,
  marketType: string,
  bettingType: string,
): EligibleBonus => {
  const marketBonus = getExchangeBettingBonusByMarketId(state, marketId);

  if (!marketBonus || !marketBonus.wallets) {
    return 0;
  }

  return calculateEligibleBonus(marketBonus, selectionId, marketType, bettingType);
};

/**
 * Creates the getExchangeLiabilityWithBonus selector
 * For a given runner and market id, returns the liability with bonus for a potential bet
 */
const createGetExchangeLiabilityWithBonus = (): OutputParametricSelector<
  BetslipApplicationState,
  BetslipExchangeRunner,
  number | undefined,
  (potentialBet: PotentialBet[], runnerTree: BetslipExchangeRunnerTree, eligibleBonus: number) => number | undefined
> => {
  const getRunnerPotentialBets = createRunnerPotentialBetsSelector();

  return createSelector(
    [
      (state: BetslipApplicationState, runner: BetslipExchangeRunner) =>
        getRunnerPotentialBets(state, runner.urn) || [],
      (state: BetslipApplicationState, runner: BetslipExchangeRunner) => getExchangeRunnerTree(state, runner.urn),
      (
        state: BetslipApplicationState,
        runner: BetslipExchangeRunner,
        marketId: string,
        marketType: string,
        bettingType: string,
      ) => getExchangeEligibleBonusByMarketId(state, marketId, runner.selectionId, marketType, bettingType),
    ],
    ([potentialBet], runnerTree, eligibleBonus): number | undefined => {
      if (!runnerTree) return undefined;

      const { size, price, side } = potentialBet;

      if (!size || !price) return undefined;

      const { market } = runnerTree;
      const { bettingType, marketType } = market;

      const bonusDiscount: BonusDiscount = {
        bonus: eligibleBonus,
      };

      return calc.liability(side, size, price, bettingType, marketType, bonusDiscount);
    },
  );
};

/**
 * For a given runner and market id, returns the potential bet with the liability calculated with bonus
 */
export const getRunnerPotentialBetWithBonus = (
  state: BetslipApplicationState,
  runner: BetslipExchangeRunner,
  marketId: string,
  marketType: string,
  bettingType: string,
): PotentialBet => {
  const [potentialBet] = createRunnerPotentialBetsSelector()(state, runner.urn);

  const getExchangeLiabilityWithBonus = createGetExchangeLiabilityWithBonus();

  const liability = getExchangeLiabilityWithBonus(state, runner, marketId, marketType, bettingType);

  return {
    ...potentialBet,
    liability,
  };
};
